import React, { useState, useEffect } from "react";
import clsx from "clsx";
import {
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  Tooltip,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlaylistAddCheck as ReasonsIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import { Section, EmptyState } from "../ui";
import { usePanelStyles, ModalTitle, SettingRow, RowActions } from "../FormFields/panel";

const useStyles = makeStyles(() => ({
  root: { display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 },
  // um único `SettingRow` dentro do cartão: sem o respiro extra da linha
  single: { paddingTop: 0, paddingBottom: 0 },
  // tabela larga rola dentro do cartão no celular
  tableWrap: { overflowX: "auto" },
  table: { minWidth: 520 },
  actionCell: { width: 110 },
}));

const FinalizacaoAtendimento = ({ settings, onSettingsChange }) => {
  const classes = useStyles();
  const p = usePanelStyles();
  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingMotivo, setEditingMotivo] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [informarValorVenda, setInformarValorVenda] = useState(false);

  useEffect(() => {
    if (settings) {
      setInformarValorVenda(settings.informarValorVenda || false);
    }
    fetchMotivos();
  }, [settings]);

  const fetchMotivos = async () => {
    try {
      const { data } = await api.get("/ticketFinalizationReasons");
      setMotivos(data);
    } catch (err) {
      console.error("Erro ao buscar motivos:", err);
    }
  };

  const handleOpenModal = (motivo = null) => {
    if (motivo) {
      setEditingMotivo(motivo);
      setFormData({
        name: motivo.name,
        description: motivo.description || "",
      });
    } else {
      setEditingMotivo(null);
      setFormData({
        name: "",
        description: "",
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingMotivo(null);
    setFormData({
      name: "",
      description: "",
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Nome do motivo é obrigatório");
      return;
    }

    setLoading(true);
    try {
      if (editingMotivo) {
        await api.put(
          `/ticketFinalizationReasons/${editingMotivo.id}`,
          formData
        );
        toast.success("Motivo atualizado com sucesso!");
      } else {
        await api.post("/ticketFinalizationReasons", formData);
        toast.success("Motivo criado com sucesso!");
      }
      handleCloseModal();
      fetchMotivos();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar motivo");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este motivo?")) {
      try {
        await api.delete(`/ticketFinalizationReasons/${id}`);
        toast.success("Motivo excluído com sucesso!");
        fetchMotivos();
      } catch (err) {
        toast.error(err.response?.data?.error || "Erro ao excluir motivo");
      }
    }
  };

  const handleInformarValorVendaChange = async (event) => {
    const newValue = event.target.checked;
    setInformarValorVenda(newValue);

    try {
      await api.put("/companySettings", {
        column: "informarValorVenda",
        data: newValue,
      });

      if (onSettingsChange) {
        onSettingsChange({ ...settings, informarValorVenda: newValue });
      }

      toast.success("Configuração atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar configuração");
      setInformarValorVenda(!newValue); // Revert on error
    }
  };

  return (
    <div className={classes.root}>
      {/* Configuração de Valor da Venda */}
      <Section title="Configurações Gerais">
        <SettingRow
          className={classes.single}
          label="Informar valor da venda ao finalizar atendimento"
          description="Quando habilitado, o campo de valor da venda aparecerá no modal de finalização"
        >
          <Switch
            checked={informarValorVenda}
            onChange={handleInformarValorVendaChange}
            color="primary"
            inputProps={{ "aria-label": "Informar valor da venda ao finalizar atendimento" }}
          />
        </SettingRow>
      </Section>

      {/* Gestão de Motivos */}
      <Section
        flush
        title="Motivos de Finalização"
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Adicionar Motivo
          </Button>
        }
      >
        {motivos.length > 0 && (
          <div className={classes.tableWrap}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="right" className={classes.actionCell}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {motivos.map((motivo) => (
                  <TableRow key={motivo.id}>
                    <TableCell className={p.cellStrong}>{motivo.name}</TableCell>
                    <TableCell className={p.cellMuted}>
                      {motivo.description || "Sem descrição"}
                    </TableCell>
                    <TableCell align="right" className={classes.actionCell}>
                      <RowActions>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenModal(motivo)}
                            aria-label={`Editar motivo ${motivo.name}`}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(motivo.id)}
                            aria-label={`Excluir motivo ${motivo.name}`}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {motivos.length === 0 && (
          <EmptyState
            icon={<ReasonsIcon />}
            title="Nenhum motivo cadastrado"
            message='Clique em "Adicionar Motivo" para começar.'
          />
        )}
      </Section>

      {/* Modal para Adicionar/Editar Motivo */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: p.dialogPaper }}
      >
        <ModalTitle
          title={editingMotivo ? "Editar Motivo" : "Adicionar Motivo"}
          subtitle="O atendente escolhe um motivo ao finalizar o atendimento."
          onClose={handleCloseModal}
        />
        <DialogContent className={clsx(p.modalBody, p.labelsAbove)}>
          <div className={p.stack}>
            <TextField
              fullWidth
              label="Nome do Motivo"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              variant="outlined"
              size="small"
              required
            />
            <TextField
              fullWidth
              label="Descrição (opcional)"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              variant="outlined"
              size="small"
              multiline
              rows={3}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading
              ? "Salvando..."
              : editingMotivo
              ? "Atualizar"
              : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FinalizacaoAtendimento;
